import './WeeklyCalendar.styles.css'

import { Button, CircularProgress } from '@mui/material'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { usePatientCareTeam } from '../../providers/CareTeam/PatientCareTeam.provider'
import {
  DrinkCounterContext,
  DrinkCounterContextInterface,
  drinkCountRecordObj,
} from '../../providers/DrinkCounter/DrinkCounterProvider'
import {
  FeatureFlagContext,
  FeatureFlagContextInterface,
} from '../../providers/FeatureFlags/FeatureFlagProvider'
import { useMessages } from '../../providers/Messages/Messages.provider'
import {
  MUMContext,
  MUMContextInterface,
  MUMRecordObj,
} from '../../providers/MUM/MUMProvider'
import {
  BACContext,
  BACContextInterface,
  BACObj,
} from '../../providers/PatientChartPage/BACProvider'
import {
  PatientChartPageContext,
  PatientChartPageContextInterface,
} from '../../providers/PatientChartPage/PatientChartPage.provider'
import {
  TrackingContext,
  TrackingContextInterface,
} from '../../providers/PatientChartPage/TrackingProvider'
import {
  SubstanceCounterContext,
  SubstanceCounterContextInterface,
} from '../../providers/SubstanceCounter/SubstanceCounter.Provider'
import { dateFormatters } from '../../utilities/HelperFunctions'
import { CareTeamModal } from './CareTeamModal'
import { MyJourneyChart } from './MyJourneyChart'
import { useStyles } from './PatientChartPage.styles'
import { MyJourneyReviewTable } from './ReviewTable/MyJourneyReviewTable'
import { ScheduleAppointment } from './ScheduleAppointment'
import { WeeklyScheduleCalendar } from './WeeklyScheduleCalendar'

export const isFloat = (num: number) => typeof num === 'number' && num % 1 !== 0

export const PatientChartPage = () => {
  dayjs.extend(utc)
  dayjs.extend(timezone)

  const { classes } = useStyles()

  const { routedPatientId } = useParams()

  const navigate = useNavigate()

  const {
    activeChartPatient,
    getPatientDetails,
    getPatientMoodRecords,
    setActiveChartPatient,
  } = useContext(PatientChartPageContext) as PatientChartPageContextInterface

  const {
    getBacRecords,
    getPatientBacDailyAverages,
    getPatientBacDailyHighest,
    bacRecordsWithVideo,
  } = useContext(BACContext) as BACContextInterface

  const { featureFlags } = useContext(
    FeatureFlagContext
  ) as FeatureFlagContextInterface

  const {
    setSelectedThreadId,
    checkIfThreadExists,
    setIsNewMessage,
    setNavigatingDirectlyToThread,
  } = useMessages()

  const { getPatientDrinkCountRecords } = useContext(
    DrinkCounterContext
  ) as DrinkCounterContextInterface

  const { MUMRecords, getPatientMUMRecords } = useContext(
    MUMContext
  ) as MUMContextInterface

  const { patientCareTeam, getPatientCareTeam } = usePatientCareTeam()

  const [showCareTeamModal, setShowCareTeamModal] = useState(false)

  const [existingThreadId, setExistingThreadId] = useState(0)

  const handleToMessageThread = () => {
    if (existingThreadId === 0) {
      setIsNewMessage(true)
      setSelectedThreadId(null)
    }
    if (existingThreadId !== 0) {
      setNavigatingDirectlyToThread(true)
      setSelectedThreadId(existingThreadId)
      setExistingThreadId(0)
      setActiveChartPatient({
        patientId: 0,
        patientName: '',
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
        vrName: '',
        dateOfBirth: '',
        admissionDate: '',
        deactivationDate: '',
        mrn: '',
        accessRoles: [],
        bacHistory: [],
        isActive: false,
        timeZone: '',
        primaryDiagnosis: '',
        secondaryDiagnoses: '',
        otherDiagnoses: '',
        lastCalibrationDate: '',
        usesRemaining: 0,
        isTapering: false,
      })
    }
    navigate('/home')
  }

  const { onboardingKitTrackingInfo, getPatientOnboardingKitTrackingInfo } =
    useContext(TrackingContext) as TrackingContextInterface

  useEffect(() => {
    if (routedPatientId) {
      getPatientDetails(parseInt(routedPatientId))
      getPatientMoodRecords(parseInt(routedPatientId))
      getPatientCareTeam(parseInt(routedPatientId))
      checkIfThreadExists(parseInt(routedPatientId)).then(setExistingThreadId)
      getPatientOnboardingKitTrackingInfo(parseInt(routedPatientId))
    }
  }, [routedPatientId])

  // 1W: Average of 1 day (7)
  // 1m: Average of 1 Day (28-31)
  // 3m Average of 1 week (13)
  // 6m Average of 1 week (26)
  const { getPatientSubstanceIntakeRecordsForProvider } = useContext(
    SubstanceCounterContext
  ) as SubstanceCounterContextInterface

  useEffect(() => {
    if (routedPatientId) {
      getPatientDetails(parseInt(routedPatientId))
    }
  }, [routedPatientId])

  // 12m Average of 2 week (26)
  const timeRangeOptions = ['1w', '1m', '3m', '6m', '12m']

  const [reviewOptions, setReviewOptions] = useState([
    'Review BAC',
    'Review Drink Types',
    'Review MUM',
    'Resources',
    'Review Substance Counter',
    'Review Drug Screens',
    'Lamictal Schedule',
  ])

  // Chart Tabs
  const [tabs, setTabs] = useState([
    'BAC',
    'DrinkCounter',
    'MoodTracker',
    'SubstanceCounter',
  ])

  const ReviewBACTable = ['Date', 'Time', 'Value', 'Reviewed', 'Reviewed By']
  const ReviewMUMTable = ['Date', 'Time', 'Reviewed', 'Reviewed By']

  const [selectedTab, setSelectedTab] = useState('BAC')
  const [selectedRecordsAll, setSelectedRecordsAll] = useState<any[]>([])
  const [selectedRecordsDailyAvg, setSelectedRecordsDailyAvg] = useState<any[]>(
    []
  )
  const [selectedRecordsDailyHigh, setSelectedRecordsDailyHigh] = useState<
    any[]
  >([])
  const [viewBacChartTab, setViewBacChartTab] = useState('Average')

  const [selectedTimeRange, setSelectedTimeRange] = useState('1w')
  const [selectedReviewOption, setSelectedReviewOption] = useState('Review BAC')

  const [videoReviewFilter, setVideoReviewFilter] = useState(false)
  const [filteredBacWithVideos, setFilteredBacWithVideos] = useState<BACObj[]>(
    []
  )
  const [filteredMUMVideos, setFilteredMUMVideos] = useState<MUMRecordObj[]>([])

  const [patientCurrentTime, setPatientCurrentTime] = useState('')

  const [chartLoading, setChartLoading] = useState(false)
  const [reviewTableLoading, setReviewTableLoading] = useState(false)
  const chartLoadingCounterRef = useRef(0)
  const reviewTableLoadingCounterRef = useRef(0)

  useEffect(() => {
    const reviewOptionsCopy = [...reviewOptions]
    featureFlags.forEach((f) => {
      if (f.enabled) {
        if (f.featureName === 'SXRI Questionnaire') {
          reviewOptionsCopy.push('Questionnaires')
        }
        if (f.featureName === 'Passport') {
          reviewOptionsCopy.push('Review Passport')
        }
      }
    })
    setReviewOptions(reviewOptionsCopy)
  }, [featureFlags])

  // This is used for featureflagging certain tab
  // if f.isEnabled, we add it to the tabs array
  //   useEffect(() => {
  //   const chartTabsCopy = [...tabs]
  //   featureFlags.forEach((f) => {
  //     if (f.enabled) {
  //     }
  //   })
  //   setTabs(chartTabsCopy)
  // }, [featureFlags])

  // Helper functions to manage loading state
  const startChartLoading = () => {
    chartLoadingCounterRef.current += 1
    setChartLoading(true)
  }

  const stopChartLoading = () => {
    chartLoadingCounterRef.current = Math.max(
      0,
      chartLoadingCounterRef.current - 1
    )
    if (chartLoadingCounterRef.current === 0) {
      setChartLoading(false)
    }
  }

  const startReviewTableLoading = () => {
    reviewTableLoadingCounterRef.current += 1
    setReviewTableLoading(true)
  }

  const stopReviewTableLoading = () => {
    reviewTableLoadingCounterRef.current = Math.max(
      0,
      reviewTableLoadingCounterRef.current - 1
    )
    if (reviewTableLoadingCounterRef.current === 0) {
      setReviewTableLoading(false)
    }
  }

  const fetchBACData = async () => {
    startChartLoading()
    try {
      if (selectedTab === 'BAC' && routedPatientId) {
        const data = await getBacRecords(Number(routedPatientId))
        setFilteredBacWithVideos(data)
        setSelectedRecordsAll(data)

        const avgRecords = await getPatientBacDailyAverages(
          Number(routedPatientId)
        )
        setSelectedRecordsDailyAvg(avgRecords)

        const highRecords = await getPatientBacDailyHighest(
          Number(routedPatientId)
        )
        setSelectedRecordsDailyHigh(highRecords)
      }
    } catch (error) {
      console.error('Error fetching BAC data:', error)
    } finally {
      stopChartLoading()
    }
  }

  const fetchDrinkCounterData = async () => {
    startChartLoading()
    try {
      if (selectedTab === 'DrinkCounter' && routedPatientId) {
        const drinkCountRecords = await getPatientDrinkCountRecords(
          Number(routedPatientId)
        )
        setSelectedRecordsAll(drinkCountRecords)
        setSelectedRecordsDailyAvg(drinkCountRecords)
      }
    } catch (error) {
      console.error('Error fetching drink counter data:', error)
    } finally {
      stopChartLoading()
    }
  }

  const fetchMoodTrackerData = async () => {
    startChartLoading()
    try {
      if (selectedTab === 'MoodTracker' && routedPatientId) {
        const moodRecords = await getPatientMoodRecords(Number(routedPatientId))
        setSelectedRecordsAll(moodRecords)
      }
    } catch (error) {
      console.error('Error fetching mood tracker data:', error)
    } finally {
      stopChartLoading()
    }
  }

  const fetchPatientSubstanceIntakeData = async () => {
    startChartLoading()
    try {
      if (selectedTab === 'SubstanceCounter' && routedPatientId) {
        const patientSubstanceIntakeRecordsData =
          await getPatientSubstanceIntakeRecordsForProvider(
            parseInt(routedPatientId)
          )

        setSelectedRecordsAll(patientSubstanceIntakeRecordsData)
      }
    } catch (error) {
      console.error('Error fetchPatientSubstanceIntakeData:', error)
    } finally {
      stopChartLoading()
    }
  }

  const fetchMUMData = async () => {
    startReviewTableLoading()
    try {
      if (selectedReviewOption === 'Review MUM' && routedPatientId) {
        const MUMRecordsData = await getPatientMUMRecords(
          Number(routedPatientId)
        )
        setFilteredMUMVideos(MUMRecordsData)
      }
    } catch (error) {
      console.error('Error fetching MUM data:', error)
    } finally {
      stopReviewTableLoading()
    }
  }

  useEffect(() => {
    fetchBACData()
  }, [])

  useEffect(() => {
    fetchBACData()
    fetchDrinkCounterData()
    fetchMoodTrackerData()
    fetchPatientSubstanceIntakeData()
  }, [selectedTab])

  // Review table data - only fetch when selectedReviewOption changes
  useEffect(() => {
    switch (selectedTab) {
      case 'BAC':
        fetchBACData()
        break

      case 'DrinkCounter':
        fetchDrinkCounterData()
        break

      case 'MoodTracker':
        fetchMoodTrackerData()
        break

      case 'SubstanceCounter':
        fetchPatientSubstanceIntakeData()
        break

      default:
        fetchBACData() // Default to BAC format
    }
  }, [selectedTab])

  // Review table data - only fetch when selectedReviewOption changes
  useEffect(() => {
    fetchMUMData()
  }, [selectedReviewOption])

  useEffect(() => {
    if (videoReviewFilter) {
      if (selectedReviewOption === 'Review BAC') {
        const filtered = bacRecordsWithVideo.filter((r: BACObj) => !r.reviewed)
        setFilteredBacWithVideos(filtered)
      }
      if (selectedReviewOption === 'Review MUM') {
        const filtered = MUMRecords.filter((v: MUMRecordObj) => !v.reviewed)
        setFilteredMUMVideos(filtered)
      }
    } else {
      if (selectedReviewOption === 'Review BAC') {
        setFilteredBacWithVideos(bacRecordsWithVideo)
      }
      if (selectedReviewOption === 'Review MUM') {
        setFilteredMUMVideos(MUMRecords)
      }
    }
  }, [videoReviewFilter])

  // update patient timezone current time
  useEffect(() => {
    if (activeChartPatient) {
      const updateTime = () => {
        try {
          const patientTimezone = activeChartPatient?.timeZone

          let time
          if (
            patientTimezone &&
            patientTimezone !== 'null' &&
            patientTimezone.trim() !== ''
          ) {
            try {
              // Validate timezone before using it
              time = dayjs().tz(patientTimezone).format('YYYY-MM-DD hh:mm A')
            } catch (timezoneError) {
              console.warn(
                `Invalid timezone: ${patientTimezone}, falling back to UTC`
              )
              time = dayjs().utc().format('YYYY-MM-DD hh:mm A')
            }
          } else {
            // Fallback to UTC if no valid timezone
            time = dayjs().utc().format('YYYY-MM-DD hh:mm A')
          }

          setPatientCurrentTime(time)
        } catch (error) {
          console.error('Error formatting time:', error)
          // Fallback to local time if all else fails
          setPatientCurrentTime(dayjs().format('YYYY-MM-DD hh:mm A'))
        }
      }

      updateTime()
      const intervalId = setInterval(updateTime, 60000)

      // Cleanup interval on component unmount
      return () => clearInterval(intervalId)
    }
  }, [activeChartPatient?.timeZone])

  const getFilteredRecords = () => {
    const sortedRecord = selectedRecordsAll.sort(
      (br1, br2) =>
        new Date(br2.recordDate).getTime() - new Date(br1.recordDate).getTime()
    )

    const last1WeekFilteredRecords = sortedRecord.filter(
      (record: BACObj | drinkCountRecordObj) => {
        const daysDiff = dayjs().diff(dayjs(record.recordDate), 'day')
        return daysDiff >= 0 && daysDiff <= 7 // Within last 7 days
      }
    )

    switch (selectedTimeRange) {
      case '1w': {
        return last1WeekFilteredRecords
      }
      case '6m': {
        const last6MonthFilteredRecords = sortedRecord.filter(
          (record: BACObj | drinkCountRecordObj) => {
            const daysDiff = dayjs().diff(dayjs(record.recordDate), 'day')
            return daysDiff >= 0 && daysDiff <= 180 // Within last 6 months
          }
        )
        return last6MonthFilteredRecords
      }
      case '1m': {
        const pastMonthRecords = sortedRecord.filter(
          (record: BACObj | drinkCountRecordObj) => {
            const daysDiff = dayjs().diff(dayjs(record.recordDate), 'day')
            return daysDiff >= 0 && daysDiff <= 30 // Within last 30 days
          }
        )
        return pastMonthRecords
      }
      case '3m': {
        const last3MonthFilteredRecords = sortedRecord.filter(
          (record: BACObj | drinkCountRecordObj) => {
            const daysDiff = dayjs().diff(dayjs(record.recordDate), 'day')
            return daysDiff >= 0 && daysDiff <= 100 // Within last 3 months
          }
        )
        return last3MonthFilteredRecords
      }
      case '12m': {
        const last1YearFilteredRecords = sortedRecord.filter(
          (record: BACObj | drinkCountRecordObj) => {
            const daysDiff = dayjs().diff(dayjs(record.recordDate), 'day')
            return daysDiff >= 0 && daysDiff <= 365 // Within last 12 months
          }
        )
        return last1YearFilteredRecords
      }
      default: {
        return last1WeekFilteredRecords
      }
    }
  }

  const renderPatientOnboardingKitTrackingInfo = () => {
    if (!onboardingKitTrackingInfo.trackingNumber) {
      return null
    }

    if (onboardingKitTrackingInfo.currentTrackingStatus) {
      return (
        <div>
          <h4 style={{ margin: '5px 0' }}>Onboarding Kit Tracking:</h4>

          {onboardingKitTrackingInfo.delivered ? (
            <span>Delivered</span>
          ) : (
            <section>
              <span>
                {dateFormatters.prettyDateWithTime(
                  onboardingKitTrackingInfo.currentTrackingDate
                )}
              </span>
              <article style={{ flexDirection: 'row' }}>
                <span>
                  📍
                  {onboardingKitTrackingInfo.currentTrackingLocation}
                  {';  '}
                </span>
                <span>{onboardingKitTrackingInfo.currentTrackingStatus}</span>
              </article>
            </section>
          )}

          <Button
            variant='outlined'
            onClick={() => {
              const fedexTrackingUrl = `https://www.fedex.com/fedextrack/?trknbr=${onboardingKitTrackingInfo.trackingNumber}`
              window.open(fedexTrackingUrl)
            }}
          >
            <span>
              Click here to track more details:{' '}
              <span>
                <b>{onboardingKitTrackingInfo.trackingNumber}</b>
              </span>
            </span>
          </Button>
        </div>
      )
    }
  }

  // get data for graph
  const getSelectedRecordsForGraph = () => {
    if (
      (selectedTab === 'BAC' || selectedTab === 'DrinkCounter') &&
      viewBacChartTab === 'Average'
    ) {
      return selectedRecordsDailyAvg
    } else if (selectedTab === 'BAC' && viewBacChartTab === 'High') {
      return selectedRecordsDailyHigh
    } else if (selectedTab === 'MoodTracker') {
      return selectedRecordsAll
    } else if (selectedTab === 'SubstanceCounter') {
      return selectedRecordsAll //todo: need update
    } else {
      return selectedRecordsDailyAvg
    }
  }

  const renderDataList = () => {
    const timeRangeRecords = getFilteredRecords()

    if (timeRangeRecords.length) {
      const getColumnsForTab = (selectedTab: string): string[] => {
        switch (selectedTab) {
          case 'BAC':
            return ['Date', 'Time', 'Value']

          case 'DrinkCounter':
            return ['Date', 'Time', 'Value', 'Goal']

          case 'MoodTracker':
            return ['Date', 'Time', 'Mood']

          case 'SubstanceCounter':
            return [
              'Date',
              'Time',
              'Substance',
              'DosageUnit',
              'IntakeAmount',
              'TotalIntake',
            ]

          default:
            return ['Date', 'Time', 'Value']
        }
      }

      if (getFilteredRecords().length) {
        return (
          <table className={classes.reviewTable}>
            <thead className={classes.tableThead}>
              <tr>
                {getColumnsForTab(selectedTab).map((l) => {
                  return (
                    <th
                      className={classes.tableTh}
                      scope='col'
                      key={`ListTable-${l}`}
                    >
                      {l}
                    </th>
                  )
                })}
              </tr>
            </thead>

            <tbody>
              {timeRangeRecords.map((fr: any, i: number) => (
                <tr key={`table-record-${i}`}>
                  <th className={classes.tableTh} scope='col'>
                    {dayjs(fr.recordDate).format('MM/DD/YYYY')}
                  </th>

                  <th className={classes.tableTh} scope='col'>
                    {dayjs.utc(fr.recordDate).local().format('h:mm A')}
                  </th>

                  {(selectedTab === 'DrinkCounter' ||
                    selectedTab === 'BAC' ||
                    selectedTab === 'MoodTracker') && (
                    <th className={classes.tableTh} scope='col'>
                      {isFloat(fr.value) ? fr.value.toFixed(3) : fr.value}
                    </th>
                  )}

                  {selectedTab === 'DrinkCounter' && (
                    <th className={classes.tableTh} scope='col'>
                      {fr.goal || 'N/A'}
                    </th>
                  )}

                  {selectedTab === 'SubstanceCounter' && (
                    <>
                      <th className={classes.tableTh} scope='col'>
                        {fr.trackedSubstance?.substance?.name || 'N/A'}
                      </th>

                      <th className={classes.tableTh} scope='col'>
                        {fr.trackedSubstance?.dosageUnit?.unitName || 'N/A'}
                      </th>

                      <th className={classes.tableTh} scope='col'>
                        {fr.intakeAmount || 'N/A'} pills
                      </th>

                      <th className={classes.tableTh} scope='col'>
                        {/* get the number of the unitName */}
                        {fr.trackedSubstance?.dosageUnit?.unitName.split(
                          ' '
                        )[0] * fr.intakeAmount}{' '}
                        {
                          // get the unit like "mg"
                          fr.trackedSubstance?.dosageUnit?.unitName.match(
                            /[a-zA-Z]+/
                          )?.[0]
                        }
                      </th>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )
      } else {
        return (
          <span className={classes.recordItemTime}>
            This patient has no record yet.
          </span>
        )
      }
    }
  }

  const daysSinceAdmission = activeChartPatient.admissionDate
    ? dayjs().diff(dayjs.utc(activeChartPatient.admissionDate), 'day')
    : null

  return (
    <div className={classes.patientChartContainer}>
      <div className='modal-container'>
        <div className='deactivate-modal'>
          <CareTeamModal
            showCareTeamModal={showCareTeamModal}
            setShowCareTeamModal={setShowCareTeamModal}
            patientCareTeam={patientCareTeam}
          />
        </div>
      </div>

      <section className={classes.patientChartInfoContainer}>
        <div className={classes.patientChartInfoCol}>
          {activeChartPatient.isTapering ? (
            <p className={classes.patientChartInfoTapering}>
              <strong>Acute Care 🚩</strong>
            </p>
          ) : (
            ''
          )}
          <p className={classes.patientChartInfoCol}>
            <strong>Name: {activeChartPatient.patientName} </strong>
          </p>

          <p className={classes.patientChartInfoCol}>
            <strong>VR Name: {activeChartPatient.vrName}</strong>
          </p>

          <p className={classes.patientChartInfoCol}>
            <strong>DOB: </strong>{' '}
            {dayjs(activeChartPatient.dateOfBirth).format('MM/DD/YYYY')}
          </p>
          <p className={classes.patientChartInfoCol}>
            <strong>MRN: </strong> {activeChartPatient.mrn}
          </p>
          {renderPatientOnboardingKitTrackingInfo()}
        </div>

        <div className={classes.patientChartInfoCol}>
          <p className={classes.patientChartInfoCol}>
            <strong>Patient TimeZone: </strong> {activeChartPatient.timeZone}
          </p>
          <p className={classes.patientChartInfoCol}>
            <strong>Patient Current Time: </strong> {patientCurrentTime}
          </p>

          {activeChartPatient.primaryDiagnosis ? (
            <p className={classes.patientChartInfoCol}>
              <strong>Primary Diagnosis: </strong>{' '}
              {activeChartPatient.primaryDiagnosis}
            </p>
          ) : null}
          {activeChartPatient.secondaryDiagnoses ? (
            <p className={classes.patientChartInfoCol}>
              <strong>Secondary Diagnosis: </strong>{' '}
              {activeChartPatient.secondaryDiagnoses}
            </p>
          ) : null}
          {activeChartPatient.otherDiagnoses ? (
            <p className={classes.patientChartInfoCol}>
              <strong>Other Diagnosis: </strong>{' '}
              {activeChartPatient.otherDiagnoses}
            </p>
          ) : null}
          {activeChartPatient.secondaryDiagnoses ? (
            <p className={classes.patientChartInfoCol}>
              <strong>Last Calibration Date: </strong>{' '}
              {dateFormatters.prettyDate(
                activeChartPatient.lastCalibrationDate
              )}
            </p>
          ) : null}
          {activeChartPatient.secondaryDiagnoses ? (
            <p className={classes.patientChartInfoCol}>
              <strong>Uses Remaining: </strong>{' '}
              {activeChartPatient.usesRemaining}
            </p>
          ) : null}
        </div>

        <div className={classes.patientChartInfoCol}>
          {activeChartPatient.program && (
            <p className={classes.patientChartInfoCol}>
              <strong>Program: </strong> {activeChartPatient.program}
            </p>
          )}

          <p className={classes.patientChartInfoCol}>
            <strong>Admission Date: </strong>{' '}
            {activeChartPatient.admissionDate || ''}{' '}
            <strong>
              {daysSinceAdmission && `(${daysSinceAdmission} days)`}
            </strong>
          </p>
          <p className={classes.patientChartInfoCol}>
            <strong>Deactivated Date: </strong>{' '}
            {activeChartPatient.deactivationDate || 'N/A'}
          </p>

          <Button
            onClick={() => {
              setShowCareTeamModal(true)
            }}
            variant='contained'
            style={{
              fontSize: '16px',
              marginTop: '10px',
              width: '175px',
            }}
          >
            View Care Team
          </Button>

          <Button
            onClick={() => {
              window.open(`zoomphonecall://${activeChartPatient.phoneNumber}`)
            }}
            variant='contained'
            style={{
              fontSize: '16px',
              marginTop: '10px',
              width: '175px',
            }}
          >
            Call via Zoom
          </Button>
        </div>

        <div>
          <p className={classes.patientChartInfoCol}>
            <strong>Schedule Appointment: </strong>{' '}
          </p>
          <ScheduleAppointment activeChartPatient={activeChartPatient} />
          <Button
            onClick={() => {
              handleToMessageThread()
            }}
            variant='contained'
            style={{
              fontSize: '16px',
              marginTop: '10px',
              width: '240px',
            }}
            disabled={activeChartPatient.patientId === 0}
          >
            Message
          </Button>
        </div>
      </section>

      <WeeklyScheduleCalendar patientId={activeChartPatient.patientId} />

      <section className={classes.patientChartDataContainer}>
        <div className={classes.patientChartDataLeft}>
          <section className={classes.patientGraphContainer}>
            <article className={classes.graphTabs}>
              {tabs.map((t, i) => (
                <button
                  className={`${classes.graphTabButton} ${
                    selectedTab === t ? classes.graphTabButtonSelected : ''
                  }`}
                  key={`patientChart-tab-${i}`}
                  type='button'
                  // selected={selectedTab === t}
                  onClick={() => setSelectedTab(t)}
                >
                  <span className={classes.graphTabButtonTxt}>{t}</span>
                </button>
              ))}
            </article>

            {selectedTab === 'BAC' && (
              <article className={classes.bacChartViewBtns}>
                <button
                  className={`${classes.bacChartViewBtn} ${
                    viewBacChartTab === 'Average'
                      ? classes.bacChartViewBtnSelected
                      : ''
                  }`}
                  onClick={() => setViewBacChartTab('Average')}
                >
                  🟪 View BAC Average
                </button>
                <button
                  className={`${classes.bacChartViewBtn} ${
                    viewBacChartTab === 'High'
                      ? classes.bacChartViewBtnSelected
                      : ''
                  }`}
                  onClick={() => setViewBacChartTab('High')}
                >
                  🟩 View BAC High
                </button>
              </article>
            )}

            <div className={classes.chart}>
              {chartLoading ? (
                <CircularProgress size={20} sx={{ margin: 'auto' }} />
              ) : (
                <MyJourneyChart
                  timeRangeOptions={timeRangeOptions}
                  selectedTab={selectedTab}
                  selectedRecordsForGraph={getSelectedRecordsForGraph()}
                  selectedTimeRange={selectedTimeRange}
                  setSelectedTimeRange={setSelectedTimeRange}
                  viewBacChartTab={viewBacChartTab}
                />
              )}
            </div>
          </section>

          <section className={classes.recordListWrapper}>
            {chartLoading ? null : renderDataList()}
          </section>
        </div>

        <section className={classes.patientChartDataRight}>
          <div className={classes.reviewGraphTabs}>
            {reviewOptions.map((t, i) => (
              <button
                className={`${classes.graphTabButton} ${
                  selectedReviewOption === t
                    ? classes.graphTabButtonSelected
                    : ''
                }`}
                key={`patientChart-tab-${i}`}
                type='button'
                // selected={selectedTab === t}
                onClick={() => setSelectedReviewOption(t)}
              >
                <span className={classes.graphTabButtonTxt}>{t}</span>
              </button>
            ))}
          </div>

          {reviewTableLoading ? (
            <CircularProgress size={20} sx={{ margin: 'auto' }} />
          ) : (
            <MyJourneyReviewTable
              routedPatientId={routedPatientId}
              selectedReviewOption={selectedReviewOption}
              ReviewBACTableTabs={ReviewBACTable}
              ReviewMUMTableTabs={ReviewMUMTable}
              videoReviewFilter={videoReviewFilter}
              setVideoReviewFilter={setVideoReviewFilter}
              filteredBacWithVideos={filteredBacWithVideos}
              setFilteredBacWithVideos={setFilteredBacWithVideos}
              filteredMUMVideos={filteredMUMVideos}
              setFilteredMUMVideos={setFilteredMUMVideos}
            />
          )}
        </section>
      </section>
    </div>
  )
}
